import { queryOptions } from '@tanstack/react-query';

import type { ParticipantType } from '@/api/core-api/types.gen';

import { pricingTeamPlansControllerGetSummaryForTeam } from '@/api/core-api';
import { queryKeys } from '@/hooks/api/query-keys';
import { getClient } from '@/utils/get-api-client';
import { PricingCreditRawUnits } from '@common/utils';

/**
 * Pricing version for a team.
 *
 * Derivation matches `services/core-api/src/controllers/organization.ts`:
 *
 * ```ts
 * if (team.newPricing) {
 *   pricingVersion = pricingTeamPlan?.pricingVersion === 3 ? 3 : 2;
 * } else {
 *   pricingVersion = 1;
 * }
 * ```
 *
 * Unit semantics on credit-side monetary fields (recruitment rate matrix,
 * `requirements.*Credits` shortfall/hold fields):
 *
 * - **v1** — legacy team (`!team.newPricing`). No active pricing plan. The
 *   sidebar is rarely hit by v1 teams; we currently treat fields as raw
 *   dollar credits (no conversion).
 * - **v2** — new-pricing team on a non-v3 plan. The rate-card matrix is
 *   informational only; the actual per-session cost is
 *   `creditRate × conversionRate(participantType)` cents (where
 *   `conversionRate = b2b ? 50 : 30`). `requirements.*Credits` are raw
 *   credits and must be multiplied by `creditRate` to get cents.
 * - **v3** — new-pricing team on a v3 plan. Rate-card matrix entries and
 *   `requirements.*Credits` are already in **cents**.
 *
 * The legacy researcher app encodes this exact split — see
 * `apps/legacy/researcher/modules/projects/project/edit/project-form-summary/project-form-summary.component.js`
 * (`getRecruitmentCostPerParticipant` v2 vs v3 branch, and the
 * `costForCredits`/`costInCents/100` conversions in
 * `getRecruitmentHoldRequired` / `getRecruitmentHoldCovered` /
 * `getAmountToPayNow`).
 */
export type TeamPricingVersion = 1 | 2 | 3;

const CREDIT_CONVERSION_RATE = {
  b2b: PricingCreditRawUnits.B2BCredits,
  b2c: PricingCreditRawUnits.B2CCredits,
} as const;

/**
 * Cents per session for a v2 plan: `creditRate × conversionRate`.
 *
 * Mirrors `pricingUtilsService.costByCreditType` in the legacy researcher app
 * (`services/active-team-plan.service.js:117-122`).
 */
export const costPerSessionCentsV2 = (
  creditRate: number,
  participantType: ParticipantType,
) => creditRate * CREDIT_CONVERSION_RATE[participantType];

/**
 * Resolve a team's pricing version + plan-level conversion fields needed to
 * display recruitment costs and prepayment shortfalls in dollars.
 *
 * The cache stores the raw summary response so the query key can be shared
 * with `useTeamCapabilities` (which selects different fields from the same
 * payload). The `select` transform produces a `TeamPricingInfo` per
 * subscription — no extra request.
 */
export const teamPlanSummaryQueryOptions = (
  teamId?: Maybe<string>,
  /**
   * Auth user id, required only when this query runs on the server (e.g. a
   * route `beforeLoad` that calls `ensureQueryData`). `getClient` is
   * isomorphic: on the server it builds an authenticated client pointed at the
   * core-api URL, in the browser it returns `undefined` and the SDK falls back
   * to the default `/v4` proxy client — so client-side callers can omit it.
   * Without it, a server-side call has no base URL and axios throws
   * "Invalid URL".
   */
  authUserId?: string,
) =>
  queryOptions({
    queryKey: queryKeys.pricing.teamPlanSummary(teamId ?? ''),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      pricingTeamPlansControllerGetSummaryForTeam({
        client: getClient(authUserId),
        path: { teamId: teamId ?? '' },
        throwOnError: true,
      })
        .then((res) => res.data)
        .then((smry) => {
          // Match legacy derivation: anything not explicitly v3 is v2 for new-pricing
          // teams. Do not gate on plan.participantType — launch UI derives audience
          // from the project, and the API can omit plan.participantType.
          if (smry.plan && smry.plan.pricingVersion == null) {
            smry.plan.pricingVersion = 2;
          }
          return smry;
        }),
  });
